"use client";

import Link from "next/link";
import {
  ArrowRight,
  Blocks,
  Building2,
  Cpu,
  FileStack,
  ShieldCheck,
  Users,
  Waypoints,
} from "lucide-react";

const principles = [
  {
    title: "Tek sistem yaklaşımı",
    description:
      "İşe alımdan çalışan yaşam döngüsüne, onboarding’den operasyonel iş akışlarına kadar dağınık süreçleri tek ürün mantığında topluyoruz.",
    icon: Blocks,
  },
  {
    title: "Kontrollü otomasyon",
    description:
      "Yapay zeka ve otomasyon katmanını insan kararının yerine değil, hızını ve tutarlılığını artıran destek katmanı olarak kurguluyoruz.",
    icon: Cpu,
  },
  {
    title: "Kurumsal güvenlik disiplini",
    description:
      "Yetki, erişim, loglama, veri izolasyonu ve operasyonel kontrol mekanizmalarını ürünün temel parçası olarak ele alıyoruz.",
    icon: ShieldCheck,
  },
  {
    title: "Operasyonel netlik",
    description:
      "İK ekiplerinin, yöneticilerin ve çalışanların aynı veri üzerinde farklı ihtiyaçlarla çalıştığını biliyor; akışları bu gerçeğe göre tasarlıyoruz.",
    icon: Users,
  },
];

const capabilities = [
  {
    title: "İşe alım ve aday akışları",
    description:
      "Aday havuzu, değerlendirme, mülakat, teklif ve çalışanlaştırma süreçlerini kopuk araçlar yerine aynı işletim modeli içinde yönetmeye odaklanıyoruz.",
    icon: Waypoints,
  },
  {
    title: "Çalışan ve organizasyon yönetimi",
    description:
      "Kullanıcı, çalışan, rol, ekip, departman, yönetici ilişkileri ve onboarding süreçlerini tek merkezden yönetilebilir hale getiriyoruz.",
    icon: Building2,
  },
  {
    title: "Belge, kayıt ve operasyon disiplini",
    description:
      "Formlar, kayıt akışları, zorunlu alanlar, doğrulama, denetim izi ve günlük operasyon ihtiyaçlarını ürün kurgusunun parçası olarak ele alıyoruz.",
    icon: FileStack,
  },
];

const commitments = [
  "Kurumsal müşteriler için ölçeklenebilir, kontrollü ve denetlenebilir bir ürün yaklaşımı",
  "Türkçe deneyim, yerel operasyon gerçekleri ve mevzuat farkındalığı",
  "Kademeli onboarding, modül bazlı erişim ve minimum sürtünmeyle kurulum",
  "Sürekli gelişen ürün yüzeyi için güçlü dokümantasyon ve operasyonel şeffaflık",
];

export default function AboutClient() {
  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,_#ffffff_0%,_#f8fafc_38%,_#eef2ff_100%)]">
      <section className="border-b border-slate-200 bg-slate-950 text-white">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-[1.25fr_0.85fr] lg:px-8 lg:py-24">
          <div className="space-y-6">
            <span className="inline-flex rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-slate-200">
              Hakkımızda
            </span>
            <div className="space-y-4">
              <h1 className="max-w-4xl text-4xl font-semibold tracking-tight text-white md:text-5xl">
                İKAI HR, insan kaynakları operasyonunu tek ve kontrollü bir
                çalışma sistemine dönüştürmek için geliştiriliyor.
              </h1>
              <p className="max-w-3xl text-base leading-7 text-slate-200 md:text-lg">
                Ürünü, yalnızca bir İK aracı değil; kullanıcı, çalışan,
                onboarding, organizasyon, aday ve operasyon akışlarını aynı
                mantık içinde birleştiren kurumsal bir çalışma altyapısı olarak
                ele alıyoruz.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 text-sm text-slate-300">
              <span className="rounded-full border border-white/15 px-3 py-1.5">
                Türkiye odaklı operasyon yaklaşımı
              </span>
              <span className="rounded-full border border-white/15 px-3 py-1.5">
                SaaS ürün modeli
              </span>
              <span className="rounded-full border border-white/15 px-3 py-1.5">
                Çok modüllü İK altyapısı
              </span>
            </div>
          </div>

          <div className="rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-300">
              Kısa Bakış
            </p>
            <div className="mt-5 space-y-4 text-sm leading-6 text-slate-100">
              <p>
                İKAI HR; işe alım, çalışan yönetimi, onboarding, erişim,
                organizasyon kurgusu, belge akışları ve operasyonel kayıt
                süreçlerini birlikte ele alan bir ürün yaklaşımı üzerine
                kuruludur.
              </p>
              <p>
                Önceliğimiz, hızlı görünen ama sürdürülemez çözümler değil;
                kurumsal ölçekte yönetilebilir, denetlenebilir ve
                genişletilebilir bir sistem kurmaktır.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {principles.map((item) => (
              <article
                key={item.title}
                className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700">
                  <item.icon className="h-6 w-6" />
                </div>
                <h2 className="mt-5 text-xl font-semibold tracking-tight text-slate-950">
                  {item.title}
                </h2>
                <p className="mt-3 text-sm leading-6 text-slate-600">
                  {item.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[1fr_1.05fr]">
            <article className="rounded-[2rem] border border-slate-200 bg-white p-7 shadow-sm md:p-8">
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
                Nasıl düşünüyoruz
              </p>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950">
                İK süreçlerinde dağınık araçlar yerine, birlikte çalışan bir
                sistem kurmak istiyoruz.
              </h2>
              <div className="mt-6 space-y-4 text-[15px] leading-7 text-slate-700">
                <p>
                  Bir organizasyonda kullanıcı hesabı, çalışan kaydı, ekip
                  ilişkileri, onboarding, aday dönüşümü, belge akışları ve
                  operasyonel onaylar birbirinden bağımsız düşünüldüğünde ürün
                  hızla karmaşıklaşır. İKAI’de bu parçaları tek veri modeli ve
                  tek akış mantığı içinde toplamaya çalışıyoruz.
                </p>
                <p>
                  Bu yaklaşım, hem ürün tutarlılığı hem de operasyonel riskin
                  azaltılması için kritik. Çünkü İK tarafında görünürde küçük
                  olan akış farkları, zamanla erişim, güvenlik, veri doğruluğu
                  ve iş yükü problemlerine dönüşür.
                </p>
                <p>
                  Bu nedenle ürün kararlarını yalnızca ekran bazlı değil, sistem
                  davranışı bazlı ele alıyoruz. Hedefimiz, ekiplerin “hangi
                  ekranda ne vardı?” sorusunu değil, “iş nasıl akar?” sorusunu
                  çözmek.
                </p>
              </div>
            </article>

            <article className="rounded-[2rem] border border-slate-950 bg-slate-950 p-7 text-white shadow-sm md:p-8">
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-400">
                Ürün odağı
              </p>
              <div className="mt-6 space-y-5">
                {capabilities.map((item) => (
                  <div
                    key={item.title}
                    className="rounded-2xl border border-white/10 bg-white/5 p-5"
                  >
                    <div className="flex items-start gap-4">
                      <div className="mt-0.5 flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 text-white">
                        <item.icon className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">
                          {item.title}
                        </h3>
                        <p className="mt-2 text-sm leading-6 text-slate-300">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-[2.5rem] border border-indigo-100 bg-[linear-gradient(135deg,_rgba(238,242,255,0.9)_0%,_rgba(255,255,255,0.96)_100%)] p-8 shadow-sm md:p-10">
            <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr]">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-indigo-700">
                  Ne Taahhüt Ediyoruz
                </p>
                <h2 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950">
                  Abartılı vaatler yerine, net ürün disiplini.
                </h2>
                <p className="mt-4 text-[15px] leading-7 text-slate-700">
                  İKAI’yi, “her şeyi çözen” bir pazarlama söylemiyle değil;
                  kurumsal ekiplerin gerçekten yönetebileceği, büyüdükçe
                  dağılmayan bir ürün çizgisiyle kurmaya çalışıyoruz.
                </p>
              </div>

              <div className="grid gap-4">
                {commitments.map((item) => (
                  <div
                    key={item}
                    className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-4"
                  >
                    <div className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-indigo-100 text-indigo-700">
                      <ArrowRight className="h-4 w-4" />
                    </div>
                    <p className="text-sm leading-6 text-slate-700">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-20 pt-8">
        <div className="mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-8">
          <div className="rounded-[2.5rem] border border-slate-200 bg-white p-8 shadow-sm md:p-10">
            <h2 className="text-3xl font-semibold tracking-tight text-slate-950">
              İKAI’yi yakından incelemek isterseniz
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-[15px] leading-7 text-slate-600">
              Kayıt olup ürünü deneyebilir, ekibiniz için kullanım senaryolarını
              bizimle değerlendirebilir veya doğrudan iletişime geçebilirsiniz.
            </p>
            <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
              <Link
                href="/register"
                className="inline-flex items-center justify-center rounded-2xl bg-indigo-600 px-6 py-3 text-sm font-semibold text-white transition hover:bg-indigo-700"
              >
                Hesap oluştur
              </Link>
              <Link
                href="/iletisim"
                className="inline-flex items-center justify-center rounded-2xl border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-900 transition hover:border-slate-400 hover:bg-slate-50"
              >
                Ekibimizle konuşun
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
